'use client';

import classnames from 'classnames/bind';
import { RtDropdown } from '@dpa/react-dropdown';
import {
  RtFormFieldChangeEvent,
  RtFormFieldText,
} from '@dpa/react-form-field-text';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Magnifyingglass2pxIcon } from '@dpa/react-icons';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { EHelpCenterType } from '@portals/help-center-core';
import { getSearchTips } from '@/api/get-search-tips';
import {
  getSearchRoute,
  TMultiPageRouteParams,
  TRouteCommon,
} from '@/_constants/routes';
import { searchStartTypingEvent } from '@/_analytic/events/search-start-typing';
import { getDocumentTitle, getIsSideMode } from '@/_utils';
import { getPageType } from '@/_analytic/_utils/get-page-type';
import { searchSubmitEvent } from '@/_analytic/events/search-submit';
import { useDebounceValue } from '@/_hooks/use-debounce-value';
import { SEARCH_AVAILABLE_COUNTRIES } from '@/_constants/common';
import { useClientEnvContext } from '@/contexts';
import { useClientSearchParams } from '@/_hooks/use-client-search-params';
import { useHelpCenterType } from '@/_hooks/use-help-center-type';
import { EPageType } from '@/_analytic/_types';
import { usePathname, useRouter } from '@/i18n/navigation';
import { searchClearEvent } from '@/_analytic/events/search-clear';
import { transformResponseDataToOptions } from './_utils/transform-response-data-to-options';
import { TResponseDataToOptions } from './_utils/transform-response-data-to-options/_types';
import { THandleSubmitOptions } from './_types';
import { MIN_SEARCH_TEXT_LENGTH } from './_constants';
import { DropdownOptions } from './_components/dropdown-options';
import styles from './index.module.scss';

const BLOCK_NAME = 'Search-Input';
const cn = classnames.bind(styles);

type TProps = {
  searchText?: string;
  isGridMode?: boolean;
  pageType?: EPageType;
};

export const SearchInput = ({
  searchText: searchTextProps,
  isGridMode,
  pageType: propsPageType,
}: TProps) => {
  const translations = useTranslations('help-center');
  const { locale } = useParams<TRouteCommon>();

  const searchTextDefault = searchTextProps || '';

  const [searchText, setSearchText] = useState<string>(searchTextDefault);
  const [searchTips, setSearchTips] = useState<Array<string>>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchOptions, setSearchOptions] = useState<
    Array<TResponseDataToOptions>
  >([]);
  const router = useRouter();

  const clientEnvs = useClientEnvContext();
  const { instructionApiUrl } = clientEnvs;

  const debouncedSearchText = useDebounceValue({
    value: searchText,
  });

  const helpCenterType = useHelpCenterType();

  const updateSearchTips = useCallback(
    async (text: string) => {
      if (text.length < MIN_SEARCH_TEXT_LENGTH) {
        setSearchTips([]);

        return;
      }

      const response = await getSearchTips({
        searchText: text,
        url: instructionApiUrl,
        language: locale,
      });

      const items = response?.result.items;
      setSearchTips(items || []);
    },
    [instructionApiUrl, locale],
  );

  useEffect(() => {
    if (debouncedSearchText) {
      updateSearchTips(debouncedSearchText);
    }
  }, [debouncedSearchText, updateSearchTips]);

  useEffect(() => {
    const options = transformResponseDataToOptions(searchTips);

    setSearchOptions(options);
  }, [searchTips]);

  const pureSearchTextLength = searchText.trim().length;

  const isShowInputHelperText =
    pureSearchTextLength >= 1 && pureSearchTextLength < MIN_SEARCH_TEXT_LENGTH;

  const params = useParams<TMultiPageRouteParams>();
  const { country } = params;
  const pathname = usePathname();
  const pageType = propsPageType || getPageType({ params, pathname });

  const searchParams = useClientSearchParams();

  const handleSubmit = ({
    value,
    onError,
    autoSuggestion,
    autoSuggestionNum,
  }: THandleSubmitOptions) => {
    const text = value || searchText;
    const trimmedSearchText = text.trim();
    const pageName = getDocumentTitle();
    searchSubmitEvent({
      pageName,
      pageType,
      query: trimmedSearchText,
      autoSuggestion: !!autoSuggestion,
      autoSuggestionNum:
        autoSuggestionNum === undefined ? -1 : autoSuggestionNum,
      helpCenterType,
    });
    if (trimmedSearchText.length < MIN_SEARCH_TEXT_LENGTH) {
      onError?.();

      return;
    }
    router.push(
      getSearchRoute({
        searchParams: { ...searchParams, searchText: text },
        country,
      }),
    );
    setShowDropdown(false);
  };

  const handleIconClick = () => {
    handleSubmit({});
  };

  const isDirty = useRef<boolean>(false);

  const handleSearchStartTyping = () => {
    const pageName = getDocumentTitle();

    if (!isDirty.current) {
      searchStartTypingEvent({
        pageType,
        pageName,
        helpCenterType,
      });
    }
    isDirty.current = true;
  };

  const handleChange = (data: RtFormFieldChangeEvent<HTMLInputElement>) => {
    setSearchText(data.value);
    handleSearchStartTyping();
    setShowDropdown(true);
  };

  const handleClickOption = (option: { id: string; subLabel?: string }) => {
    setSearchText(option.subLabel || '');
    handleSubmit({
      value: option.subLabel,
      autoSuggestion: true,
      // todo https://youtrack.wildberries.ru/issue/INS-881/Privesti-v-poryadok-linter
      autoSuggestionNum: parseInt(option.id),
    });
  };

  const renderCustomOption = (
    <DropdownOptions
      options={searchOptions}
      onChange={handleClickOption}
      searchingText={searchText}
    />
  );

  const idDropdownOpened = searchText && !isShowInputHelperText && showDropdown;

  const hint = translations('searchInputHelper', {
    symbolsAmount: MIN_SEARCH_TEXT_LENGTH,
  });
  const canSeeSearchResults = Boolean(idDropdownOpened && searchOptions.length);

  const disableSearch = !SEARCH_AVAILABLE_COUNTRIES.includes(country);
  const placeholder = disableSearch
    ? translations('disabledSearchInputPlaceholder')
    : translations('searchInputPlaceholder');

  const isSideMode = getIsSideMode(searchParams);
  const canSeeSearch = !isSideMode || (isSideMode && !disableSearch);
  const details = isShowInputHelperText ? hint : '';

  const handleSearchClear = () => {
    searchClearEvent({
      tipsFound: searchOptions.length,
      isEmptyTips: searchOptions.length === 0,
      helpCenterType: EHelpCenterType.MAIN,
      query: searchText,
      locale,
    });
  };

  if (!canSeeSearch) {
    return null;
  }

  return (
    <div
      className={cn(BLOCK_NAME, {
        [`${BLOCK_NAME}--grid-mode`]: isGridMode,
      })}
    >
      <div className={cn(`${BLOCK_NAME}__input`)}>
        <RtDropdown
          model="full"
          position="bottom"
          slot={renderCustomOption}
          trigger={['manual']}
          use="generic"
          show={canSeeSearchResults}
          distance="near"
        >
          <RtFormFieldText
            id="search-input"
            onChange={handleChange}
            name="search-input"
            autoComplete="off"
            placeholder={placeholder}
            phDisplay="hidden"
            size="lg"
            ui="normal"
            use="neutral"
            leadingIcon={Magnifyingglass2pxIcon}
            value={searchText}
            onPressEnter={handleIconClick}
            details={details}
            detailsSpecification="variant"
            onClear={handleSearchClear}
            clearable="persistent"
          />
        </RtDropdown>
      </div>
    </div>
  );
};
